/**
 * Treatment model for both basic therapies and special treatment projects
 */
import { v4 as uuidv4 } from 'uuid';

// Treatment types
export const TREATMENT_TYPES = {
  ACUPUNCTURE: '针灸',
  MASSAGE: '推拿',
  MOXIBUSTION: '拔罐',
  CUPPING: '刮痧',
  HERBAL: '中药',
  SCRAPING: '拔罐',
  OTHER: '其他',
  PROJECT: '特色项目', // For special treatment projects
};

class Treatment {
  /**
   * Constructor
   * @param {Object} treatmentData - Treatment data
   */
  constructor(treatmentData = {}) {
    this.id = treatmentData.id || uuidv4();
    this.name = treatmentData.name || '';
    this.description = treatmentData.description || '';
    // 'basic' for basic therapy, 'project' for special treatment project
    this.type = treatmentData.type || 'basic';
    this.duration = treatmentData.duration || 30; // in minutes
    this.price = treatmentData.price || 0;
    this.practitioners = treatmentData.practitioners || []; // array of practitioner IDs
    this.isActive = treatmentData.isActive !== undefined ? treatmentData.isActive : true;
    this.tags = treatmentData.tags || [];
    this.applicableConditions = treatmentData.applicableConditions || [];
    this.contraindications = treatmentData.contraindications || [];
    this.preparationInstructions = treatmentData.preparationInstructions || '';
    this.postTreatmentCare = treatmentData.postTreatmentCare || '';
    this.image = treatmentData.image || null;
    this.createdAt = treatmentData.createdAt || new Date().toISOString();
    this.updatedAt = treatmentData.updatedAt || new Date().toISOString();
    // For special treatment projects: array of basic treatment IDs
    this.composedOf = treatmentData.composedOf || [];
  }

  /**
   * Update treatment data
   * @param {Object} treatmentData - Treatment data to update
   * @returns {Treatment} Updated treatment instance
   */
  update(treatmentData) {
    Object.keys(treatmentData).forEach(key => {
      if (treatmentData[key] !== undefined && key !== 'id') {
        this[key] = treatmentData[key];
      }
    });
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Add a practitioner who can perform this treatment
   * @param {string} practitionerId - Practitioner ID
   * @returns {boolean} Whether practitioner was added
   */
  addPractitioner(practitionerId) {
    if (!this.practitioners.includes(practitionerId)) {
      this.practitioners.push(practitionerId);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Remove a practitioner
   * @param {string} practitionerId - Practitioner ID
   * @returns {boolean} Whether practitioner was removed
   */
  removePractitioner(practitionerId) {
    const index = this.practitioners.indexOf(practitionerId);
    if (index !== -1) {
      this.practitioners.splice(index, 1);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Add applicable medical condition
   * @param {string} condition - Medical condition
   * @returns {boolean} Whether condition was added
   */
  addApplicableCondition(condition) {
    if (!this.applicableConditions.includes(condition)) {
      this.applicableConditions.push(condition);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Add contraindication
   * @param {string} contraindication - Contraindication
   * @returns {boolean} Whether contraindication was added
   */
  addContraindication(contraindication) {
    if (!this.contraindications.includes(contraindication)) {
      this.contraindications.push(contraindication);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Add tag to treatment
   * @param {string} tag - Tag to add
   * @returns {boolean} Whether tag was added
   */
  addTag(tag) {
    if (!this.tags.includes(tag)) {
      this.tags.push(tag);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Remove tag from treatment
   * @param {string} tag - Tag to remove
   * @returns {boolean} Whether tag was removed
   */
  removeTag(tag) {
    const index = this.tags.indexOf(tag);
    if (index !== -1) {
      this.tags.splice(index, 1);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Calculate the end time based on start time and duration
   * @param {Date|string} startTime - Start time
   * @returns {Date} End time
   */
  calculateEndTime(startTime) {
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + this.duration);
    return end;
  }

  /**
   * Activate treatment
   * @returns {Treatment} Updated treatment instance
   */
  activate() {
    this.isActive = true;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Deactivate treatment
   * @returns {Treatment} Updated treatment instance
   */
  deactivate() {
    this.isActive = false;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Convert to JSON for storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      type: this.type,
      duration: this.duration,
      price: this.price,
      practitioners: this.practitioners,
      isActive: this.isActive,
      tags: this.tags,
      applicableConditions: this.applicableConditions,
      contraindications: this.contraindications,
      preparationInstructions: this.preparationInstructions,
      postTreatmentCare: this.postTreatmentCare,
      image: this.image,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      composedOf: this.composedOf,
    };
  }

  /**
   * Create treatment from JSON data
   * @param {Object} json - JSON data
   * @returns {Treatment} Treatment instance
   * @static
   */
  static fromJSON(json) {
    return new Treatment(json);
  }
}

export default Treatment;